<script setup lang="ts">
import ScenarioSideBar from '@/components/scenario-form/ScenarioSideBar.vue'
import { useScenarioStore } from '@/stores/scenario'
import { storeToRefs } from 'pinia'
import { computed, markRaw, onMounted, ref } from 'vue'
import type { ScenarioFormTab, ScenarioFormTabs } from '@/components/scenario-form/type/type'
import GeneralForm from '@/components/scenario-form/GeneralForm.vue'
import ScenarioOptionsDetail from '@/components/scenario-form/ScenarioOptionsDetail.vue'
import { useRoute } from 'vue-router'
import DataDownloader from '@/components/scenario-form/DataDownloader.vue'

const route = useRoute()

const scenarioStore = useScenarioStore()
const { currentItem: currentScenarioItem } = storeToRefs(scenarioStore)
const isFieldsReady = ref(false)
const tab = ref<ScenarioFormTab>('DATA_DOWNLOADER')
const tabs = ref<ScenarioFormTabs>([
  {
    value: 'DATA_DOWNLOADER',
    label: 'Data preparation',
    isCompleted: false,
    isShown: true,
    component: markRaw(DataDownloader)
  },
  {
    value: 'PROJECT_DETAILS',
    label: 'Simulation details',
    isCompleted: false,
    isShown: true,
    component: markRaw(GeneralForm)
  },
  {
    value: 'CALCULATION_SELECTION',
    label: 'Calculation selection',
    isCompleted: false,
    isShown: true,
    component: markRaw(ScenarioOptionsDetail)
  }
])

const hideRunSimulation = ref(false)

function checkHideRunSimulation() {
  hideRunSimulation.value = currentScenarioItem.value?.status !== draftStatusValue.value
}

const statusChoices = ref<{ value: string; display_name: string }[]>([])
onMounted(async () => {
  if (!currentScenarioItem.value) {
    const scenarioId = parseInt(route.params.scenarioId as string)
    await scenarioStore.fetchOptions()
    await scenarioStore.fetchItem(scenarioId)
  }
  const options = await scenarioStore.getScenarioOptions?.()
  statusChoices.value = options?.actions?.POST?.status?.choices || []

  setRequiredFields()
  isFieldsReady.value = true

  checkHideRunSimulation()
  if (
    currentScenarioItem.value &&
    currentScenarioItem.value.status !==
      (statusChoices.value.find((choice) => choice.display_name === 'Draft')?.value || 'Draft')
  ) {
    tabs.value.forEach((tab) => (tab.isCompleted = true))
  }
})

const draftStatusValue = computed(() => {
  return statusChoices.value.find((choice) => choice.display_name === 'Draft')?.value || 'Draft'
})

const updateTabValue = (value: ScenarioFormTab) => {
  if (tab.value === 'DATA_DOWNLOADER') {
    updateTabCompletion('DATA_DOWNLOADER', true)
  }
  tab.value = value
}

const updateTabCompletion = (value: ScenarioFormTab, isCompleted: boolean) => {
  tabs.value.find((tab) => tab.value === value)!.isCompleted = isCompleted
}

const setRequiredFields = () => {
  const requiredKeys = [
    'origin_type',
    'networks',
    'origin',
    'destinations',
    'destination_type',
    'disruption_type'
  ]
  if (scenarioStore.fields != null) {
    requiredKeys.forEach((key) => {
      if (scenarioStore.fields && scenarioStore.fields[key]) {
        scenarioStore.fields[key].required = true
      }
    })
  }
}

const isReadOnly = computed(() => {
  return currentScenarioItem.value?.status !== draftStatusValue.value
})
</script>

<template>
  <ScenarioSideBar
    :tab="tab"
    :tabs="tabs"
    @update-tab-value="updateTabValue"
    :hideRunSimulation="hideRunSimulation"
  />

  <template v-if="!currentScenarioItem">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>

  <v-window v-else v-model="tab" class="px-8 py-6">
    <v-window-item v-for="item in tabs" :key="item.value" :value="item.value">
      <component
        v-if="currentScenarioItem && isFieldsReady"
        :is="item.component"
        :tab="item.value"
        :disabled="false"
        :readonly="isReadOnly"
        :project-id="currentScenarioItem.project"
        :scenario-id="currentScenarioItem.id"
        @update-tab-completion="updateTabCompletion"
        @change-tab="updateTabValue"
      >
      </component>
    </v-window-item>
  </v-window>
</template>

<style scoped></style>
